import { FC, useContext, useEffect, useState } from 'react';
import { Input, Modal, Radio, Segmented, Spin } from 'antd';
import styled from '@emotion/styled';
import { colorConstants } from 'config/colorConstants';
import { EditModalType } from '../ScenarioTableWrapper';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import { EditableFieldsType } from 'types/pricingSimulator';
import { antIcon } from 'components/molecules/Filter/FilterApplyButton';
import { UserContext } from 'contexts/ConfigContext';
import { fetchCurrency } from 'utils/helperMethods';

export const inputTileData = [
    { value: 'incremental', label: 'Increment Abs. Change' },
    { value: 'percentage', label: '% Change' },
    { value: 'absolute', label: 'Set Final Value' },
];

const CustomSegmented = styled(Segmented)`
    .ant-segmented-item-selected {
        border-radius: 4px;
    }
    .ant-segmented-item {
        flex: 1;
    }
    .ant-segmented-group {
        justify-content: space-between;
    }
`;

export const CustomModal = styled(Modal)`
    .ant-modal-content {
        border-radius: 8px !important;
    }
    .ant-modal-header {
        border-radius: 8px 8px 0px 0px !important;
        border-bottom: none !important;
    }
    .ant-modal-footer {
        border-top: none !important;
    }
    .ant-modal-close-icon {
        position: relative;
        top: -2px;
    }
    .ant-modal-title {
        font-weight: 400 !important;
    }
    .ant-modal-body {
        padding: 16px !important;
        padding-top: 0px !important;
    }
`;

type Props = {
    data: EditModalType;
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitClick: (data: any) => void;
    loading: boolean;
};

const ScenarioColumnEditModal: FC<Props> = ({ data, onCancel, open, onSubmitClick, loading }) => {
    const [inputTile, setInputTile] = useState(inputTileData[0]);
    const [applyValue, setApplyValue] = useState<EditableFieldsType>(data?.from === 'cell' ? 'cell' : 'all');
    const [value, setValue] = useState('');

    const { dimensionCheckboxObj, selectedScenarioInfo, isDropdownAtLowestLevel } =
        useContext(pricingSimulatorDataContext);
    const { appConfig } = useContext(UserContext);
    const { data: kpiData } = data;

    const currency = fetchCurrency(appConfig);

    useEffect(() => {
        //set radio button
        if (open) {
            setApplyValue(data?.from === 'cell' ? 'cell' : 'all');
        }
        //reset popup content to default on popup close
        return () => {
            setInputTile(inputTileData[0]);
            setApplyValue(data?.from === 'cell' ? 'cell' : 'all');
            setValue('');
        };
    }, [open]);

    return (
        <CustomModal
            style={{ borderRadius: 8 }}
            cancelButtonProps={{ style: { borderRadius: 6 } }}
            okButtonProps={{ disabled: !value || isNaN(Number(value)), style: { borderRadius: 6 } }}
            onOk={() => {
                if (loading) return;
                onSubmitClick({
                    kpiData,
                    value,
                    inputTile,
                    applyValue,
                });
            }}
            okText={loading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : 'Apply'}
            destroyOnClose
            centered
            open={open}
            width={420}
            onCancel={(e) => {
                if (loading) return;
                onCancel(e);
            }}
            title={`Edit: ${kpiData?.kpiName}`}
        >
            <div className="flex-col flex gap-1">
                {/* Tabs section */}
                <CustomSegmented
                    //block
                    style={{ fontSize: 12, borderRadius: 4 }}
                    onChange={(val) => {
                        const selectedItem = inputTileData?.find((item) => {
                            return item?.value === val;
                        });
                        if (selectedItem) setInputTile(selectedItem);
                    }}
                    value={inputTile.value}
                    options={inputTileData.filter(
                        (i: any) => isDropdownAtLowestLevel || (!isDropdownAtLowestLevel && i.value !== 'absolute'), // at higher level remove absolute option
                    )}
                />
                <div className="flex flex-col px-2 py-4 gap-4 border border-backgroundGreyAlt6 rounded">
                    {/* INPUT section */}
                    <div className="flex items-center">
                        <p className="text font-xs mr-2 w-[180px] text-right text-blackAlt flex items-center  justify-end">
                            <span className="mr-1 text-blackAlt">{inputTile.label}</span>{' '}
                            {/* <QuestionCircleOutlined style={{ color: 'gray', marginRight: 2 }} /> */}:
                        </p>
                        <div className="relative flex gap-2 justify-center items-center">
                            <Input
                                type="number"
                                value={value}
                                style={{ fontSize: 13, width: 128, borderRadius: 6 }}
                                placeholder="Input"
                                onChange={(e: any) => setValue(e.target.value)}
                            />
                            <span className="font-xs text-fontGreyAlt ">
                                {inputTile.value === inputTileData[1].value ? '%' : currency}
                            </span>
                        </div>
                    </div>

                    {/* RADIO BUTTONS section */}
                    <Radio.Group
                        onChange={(e) => setApplyValue(e.target.value)}
                        value={applyValue}
                        style={{ display: 'flex', flexDirection: 'column', marginLeft: '10px' }}
                    >
                        <Radio
                            disabled={data?.from !== 'cell'}
                            style={{ fontSize: 12, color: colorConstants.blackAlt1 }}
                            value={'cell'}
                        >
                            Apply to the Cell Only
                        </Radio>

                        <Radio
                            disabled={Object.keys(dimensionCheckboxObj || {}).length <= 0}
                            style={{ fontSize: 12, color: colorConstants.blackAlt1 }}
                            value={'selected'}
                        >
                            Apply to Selected Only
                        </Radio>
                        <Radio
                            style={{ fontSize: 12, color: colorConstants.blackAlt1 }}
                            value={'all'}
                        >{`Apply to All in ${
                            kpiData?.kpiName
                        } ( ${selectedScenarioInfo?.count?.toLocaleString()} items )`}</Radio>
                    </Radio.Group>
                </div>
            </div>
        </CustomModal>
    );
};

export default ScenarioColumnEditModal;
